/**
 * Search Footer
 *
 * Visible result count + accessible live region for screen readers.
 * Shows "X of Y results" when paginated, "X results" otherwise.
 */

import { Box, HStack, Text } from '@chakra-ui/react';

interface SearchLiveRegionProps {
  /** null means "do not announce a count" - see countAnnounceable. */
  resultCount: number | null;
  totalCount?: number;
  debouncedQuery: string;
  /** Announced in place of a count when the search request failed. */
  failureMessage?: string | null;
}

/** "12 of 40 results" when paginated, "12 results" otherwise, null when there
 *  is no count to state (Navigation / Settings / Help scopes). */
function resultLabel(resultCount: number | null, totalCount?: number): string | null {
  if (resultCount === null) return null;
  if (totalCount !== undefined && totalCount > resultCount) {
    return `${resultCount} of ${totalCount} results`;
  }
  return `${resultCount} result${resultCount !== 1 ? 's' : ''}`;
}

export function SearchLiveRegion({
  resultCount,
  totalCount,
  debouncedQuery,
  failureMessage,
}: SearchLiveRegionProps) {
  const hasQuery = debouncedQuery.length > 0;

  const label = resultLabel(resultCount, totalCount);

  return (
    <>
      {/* Visible footer — tinted bg distinct from the white result body so it
          reads as a separate functional zone (matches the reference design). */}
      {hasQuery && label !== null && (
        <HStack
          data-palette-footer
          px={4}
          py={2.5}
          bg="bg.subtle"
          borderTop="1px solid"
          borderColor="border.muted"
          justify="space-between"
        >
          <Text fontSize="xs" color="text.muted">
            {label}
          </Text>
        </HStack>
      )}

      {/* Screen-reader live region.
          Mounted UNCONDITIONALLY — only its contents are conditional. A live
          region has to be present and quiet BEFORE its text changes for the
          change to be announced; inserting one that already has content in it
          announces nothing. Returning null when there is no count (Navigation
          / Settings / Help scopes) tore the region out, so the first count
          after any scope round-trip went silently unannounced. */}
      <Box aria-live="polite" aria-atomic position="absolute" w="1px" h="1px" overflow="hidden">
        {failureMessage ?? (hasQuery && label !== null ? `${label} found` : '')}
      </Box>
    </>
  );
}
